import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  InvalidFormField,
  LocationNotFound,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { InventoryItem, type LinkOffer, type StockCard, StockedItem } from "../model/inventory";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const ItemsResponse = z.object({ items: z.array(InventoryItem) });
const LinkedResponse = z.object({ success: z.literal(true) });

/** `/stores/:storeId/inventory`: what the shop has for sale, whichever provider (or none) holds it. */
export class InventoryApi {
  constructor(private readonly t: Transport) {}

  /** This app's offers plus, for a Shopify-connected store, the products it never created. */
  async list(ctx: ApiContext, storeId: string): Promise<Result<InventoryItem[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/inventory`);
    return forward(
      r,
      (j) => parse(ItemsResponse, j).items,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Stocks a confirmed card as one lot. A card already listed is `InvalidFormField` on `cardId`. */
  async stock(
    ctx: ApiContext,
    storeId: string,
    item: StockCard,
  ): Promise<
    Result<StockedItem, ApiNotAuthorized | StoreNotFound | LocationNotFound | StoreRoleForbidden | InvalidFormField>
  > {
    const r = await this.t.post(ctx, `/stores/${storeId}/inventory`, { body: item });
    return forward(
      r,
      (j) => parse(StockedItem, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "locationId") return new LocationNotFound(item.locationId ?? "", e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Adopts a product that already exists in Shopify as the offer's listing. */
  async link(
    ctx: ApiContext,
    storeId: string,
    link: LinkOffer,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/inventory/link`, { body: link });
    return forward(
      r,
      (j) => {
        parse(LinkedResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }
}
